import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../../hooks/formatDateTime";
import { resolveImageUrl, sanitizeCmsHtml } from "./homepageHero.helpers";

const DEVICES = {
  desktop: { label: "Desktop", width: 1920, height: 720 },
  tablet: { label: "Tablet", width: 1024, height: 760 },
  mobile: { label: "Mobile", width: 760, height: 960 },
};

function chooseImage(slide, device) {
  const priorities = {
    desktop: [["desktopImage", "desktop"], ["tabletImage", "tablet"], ["mobileImage", "mobile"]],
    tablet: [["tabletImage", "tablet"], ["desktopImage", "desktop"], ["mobileImage", "mobile"]],
    mobile: [["mobileImage", "mobile"], ["tabletImage", "tablet"], ["desktopImage", "desktop"]],
  };
  const match = priorities[device].find(([field]) => Boolean(slide[field]));
  return match ? { url: resolveImageUrl(slide[match[0]]), source: match[1] } : { url: "", source: null };
}

function previewButtonStyle(button) {
  if (button.style === "OUTLINE") {
    return { color: button.textColor, backgroundColor: "transparent", borderColor: button.borderColor };
  }
  if (button.style === "TEXT") {
    return { color: button.textColor, backgroundColor: "transparent", borderColor: "transparent" };
  }
  return { color: button.textColor, backgroundColor: button.backgroundColor, borderColor: button.borderColor };
}

function SlideCanvas({ slide, device, language }) {
  const config = DEVICES[device];
  const image = chooseImage(slide, device);
  const layoutValue = slide[`${device}Layout`] || slide.contentPosition;
  const horizontal = ["start", "center", "end"].includes(layoutValue) ? layoutValue : "start";
  const verticalValue = slide[`${device}VerticalLayout`];
  const vertical = ["start", "center", "end"].includes(verticalValue) ? verticalValue : "center";
  const buttons = [...(slide.buttons || [])].sort((a, b) => Number(a.order) - Number(b.order));
  const title = language === "nl" ? slide.title_nl : slide.title;
  const subtitle = language === "nl" ? slide.subtitle_nl : slide.subtitle;

  return (
    <div
      className={`hero-device-canvas hero-device-canvas--${device} hero-device-canvas--horizontal-${horizontal} hero-device-canvas--vertical-${vertical}`}
      style={{ width: config.width, height: config.height }}
    >
      {image.url ? <img className="hero-device-canvas__image" src={image.url} alt={`${config.label} hero artwork`} /> : <div className="hero-device-canvas__placeholder"><span>▧</span><strong>Upload {config.label.toLowerCase()} image</strong><small>{config.width} × {config.height}</small></div>}
      <div className="hero-device-canvas__overlay" />
      <div className="hero-device-canvas__content">
        <div className="hero-device-canvas__copy">
          <div className="hero-device-canvas__title" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(title) }} />
          <div className="hero-device-canvas__subtitle" dangerouslySetInnerHTML={{ __html: sanitizeCmsHtml(subtitle) }} />
          {buttons.length > 0 && (
            <div className="hero-device-canvas__buttons">
              {buttons.map((button) => (
                <button key={button.id || `${button.order}-${button.label}`} type="button" className={`is-${button.style.toLowerCase()}`} style={previewButtonStyle(button)} onClick={(event) => { event.preventDefault(); event.stopPropagation(); }}>
                  {(language === "nl" ? button.label_nl : button.label) || "Untitled button"}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ScaledDeviceFrame({ slide, device, language, large = false }) {
  const config = DEVICES[device];
  const measureRef = useRef(null);
  const [scale, setScale] = useState(0.25);

  useEffect(() => {
    const element = measureRef.current;
    if (!element) return undefined;
    const updateScale = () => setScale(Math.min(1, element.clientWidth / config.width));
    updateScale();
    const observer = new ResizeObserver(updateScale);
    observer.observe(element);
    return () => observer.disconnect();
  }, [config.width]);

  return (
    <div className={`hero-device-frame hero-device-frame--${device} ${large ? "is-large" : ""}`}>
      {device === "desktop" && <div className="hero-device-frame__browser"><i /><i /><i /><span>Storefront preview</span></div>}
      {device === "tablet" && <span className="hero-device-frame__camera" aria-hidden="true" />}
      {device === "mobile" && <span className="hero-device-frame__speaker" aria-hidden="true" />}
      <div ref={measureRef} className="hero-device-frame__measure" style={{ height: config.height * scale }}>
        <div className="hero-device-frame__scaled" style={{ width: config.width, height: config.height, transform: `scale(${scale})` }}>
          <SlideCanvas slide={slide} device={device} language={language} />
        </div>
      </div>
      {device !== "desktop" && <span className="hero-device-frame__home" aria-hidden="true" />}
    </div>
  );
}

export default function DeviceSlidePreview({ slide }) {
  const { i18n } = useTranslation();
  const [device, setDevice] = useState("desktop");
  const [language, setLanguage] = useState("en");
  const [largePreview, setLargePreview] = useState(false);
  const image = useMemo(() => chooseImage(slide, device), [slide, device]);

  return (
    <aside className="hero-preview-sidebar">
      <div className="hero-preview-heading">
        <div><strong>Storefront preview</strong><span>Live preview from the current slide values</span></div>
        <button type="button" className="hero-preview-expand" onClick={() => setLargePreview(true)}>Open large preview</button>
      </div>

      <div className="hero-device-switch" role="tablist" aria-label="Preview device">
        {Object.entries(DEVICES).map(([key, config]) => (
          <button key={key} id={`hero-preview-tab-${key}`} type="button" role="tab" aria-selected={device === key} aria-controls="hero-device-preview-panel" className={device === key ? "is-active" : ""} onClick={() => setDevice(key)}>{config.label}</button>
        ))}
      </div>
      <div className="hero-preview-language-switch" role="tablist" aria-label="Preview language">
        <button type="button" role="tab" aria-selected={language === "en"} className={language === "en" ? "is-active" : ""} onClick={() => setLanguage("en")}>English</button>
        <button type="button" role="tab" aria-selected={language === "nl"} className={language === "nl" ? "is-active" : ""} onClick={() => setLanguage("nl")}>Dutch</button>
      </div>

      <div id="hero-device-preview-panel" role="tabpanel" aria-labelledby={`hero-preview-tab-${device}`} className="hero-device-preview-panel">
        {image.source && image.source !== device && <div className="hero-preview-fallback-warning">Using {image.source} image as fallback</div>}
        <ScaledDeviceFrame slide={slide} device={device} language={language} />
      </div>

      <dl className="hero-preview-summary">
        <div><dt>Status</dt><dd>{slide.isActive ? "Enabled" : "Disabled"}</dd></div>
        <div><dt>Audience</dt><dd>{slide.audienceType}</dd></div>
        <div><dt>Starts</dt><dd>{slide.is_permanent ? "Permanent" : slide.startDateTime ? formatDateTime(slide.startDateTime, i18n, false) : "Not set"}</dd></div>
        <div><dt>Ends</dt><dd>{slide.is_permanent ? "No end date" : slide.endDateTime ? formatDateTime(slide.endDateTime, i18n, false) : "Not set"}</dd></div>
      </dl>

      <Modal show={largePreview} onHide={() => setLargePreview(false)} fullscreen className="hero-large-preview-modal">
        <Modal.Header closeButton><Modal.Title>{DEVICES[device].label} storefront preview</Modal.Title></Modal.Header>
        <Modal.Body><ScaledDeviceFrame slide={slide} device={device} language={language} large /></Modal.Body>
      </Modal>
    </aside>
  );
}
